import { makeStyles } from '@material-ui/styles'
import { calculateMinMaxPorts } from 'app/plugins/openstack/components/networks/security-groups/helpers'
import { portRangeValidator } from 'app/plugins/openstack/components/networks/security-groups/SecurityGroupRulesParamFields'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import DropdownField from 'core/components/validatedForm/DropdownField'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import TextField from 'core/components/validatedForm/TextField'
import Card from 'core/elements/card'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import useFieldsArrayManager from 'core/hooks/useFieldsArrayManager'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import Theme from 'core/themes/model'
import { nameValidator, validIpv4Validator } from 'core/utils/fieldValidators'
import ProtocolPicklist from 'k8s/components/clusters-dashboard/ProtocolPicklist'
import { updateKaapiClusterAddon } from 'k8s/components/kaapi/cluster-addons/actions'
import React, { useCallback, useEffect } from 'react'

const defaultAllowedAddresses = { ipAddress: '' }
const defaultPortDetails = { name: '', portRange: '', protocol: '', remoteAddressPool: '' }

const defaultParams = {
  addressPools: [],
  portDetails: [],
}

const getMetallbConfigBody = (params) => {
  return {
    spec: {
      clusterSpec: {
        firewallConfig: {
          allowedAddressPools: params?.addressPools?.map((addr) => addr?.ipAddress) || [],
          inboundPorts:
            params?.portDetails?.map((port) => {
              const { portMinRange, portMaxRange } = calculateMinMaxPorts(port?.portRange || '')
              return {
                portMin: portMinRange,
                portMax: portMaxRange,
                name: port?.name,
                protocol: port?.protocol,
                remoteAddressPool: port?.remoteAddressPool,
              }
            }) || [],
        },
      },
    },
  }
}
const ConfigureMetalLBModal = ({ onClose, rows: clusters }) => {
  const { params, updateParams } = useParams(defaultParams)
  const { update: updateAddonFn, updating, error, reset } = useUpdateAction(updateKaapiClusterAddon)
  const classes = useStyles()

  useEffect(() => {
    const cluster = clusters && clusters[0]
    if (cluster) {
      const { allowedAddressPools, inboundPorts } =
        cluster?.metalLBAddon?.spec?.clusterSpec?.firewallConfig || {}
      updateParams({
        addressPools: allowedAddressPools?.map((addr) => ({ ipAddress: addr })) || [],
        portDetails:
          inboundPorts?.map((port) => ({
            name: port?.name,
            portRange: `${port?.portMin}-${port?.portMax}`,
            protocol: port?.protocol,
            remoteAddressPool: port?.remoteAddressPool,
          })) || [],
      })
    }
  }, [clusters])

  const allowedAddressesOps = useFieldsArrayManager(
    params.addressPools,
    (arr) => updateParams({ addressPools: arr }),
    defaultAllowedAddresses,
  )

  const portDetailsOps = useFieldsArrayManager(
    params.portDetails,
    (arr) => updateParams({ portDetails: arr }),
    defaultPortDetails,
  )

  const handleSubmit = useCallback(async () => {
    const body = getMetallbConfigBody(params)

    const { success } = await updateAddonFn({
      namespace: clusters[0]?.metadata?.namespace,
      customBody: body,
      name: `metallb-addon-${clusters[0]?.metadata?.name}`,
    })
    if (success) {
      reset()
      onClose()
    }
  }, [clusters, params])

  return (
    <ModalForm
      open
      onClose={onClose}
      submitTitle="Submit"
      title="Expose Load Balancer services"
      onSubmit={handleSubmit}
      submitting={updating}
      error={error}
    >
      <FormFieldSection
        title="Allowed Addresses"
        info="Enter Load Balancer IPs, (e.g from MetalLB IP address pool)"
      >
        {params?.addressPools?.map((addressPool, idx) => (
          <div key={idx} className={classes.addressFieldContainer}>
            <TextField
              id={`allowedAddressPairs.${idx}.ipAddress`}
              label="IP Address or CIDR"
              onChange={(value) => {
                allowedAddressesOps?.updateAt({
                  idx,
                  field: addressPool,
                  values: { ipAddress: value },
                })
              }}
              value={addressPool?.ipAddress}
              placeholder="eg. 12.12.11.12"
              required
              validations={[validIpv4Validator]}
            />

            <div className={classes.removePair} onClick={() => allowedAddressesOps?.removeAt(idx)}>
              <FontAwesomeIcon className={classes.icon} size="lg" solid>
                circle-minus
              </FontAwesomeIcon>
            </div>
          </div>
        ))}
        <div className={classes.operationLabel} onClick={allowedAddressesOps.add}>
          <FontAwesomeIcon className={classes.icon} size="lg" solid>
            circle-plus
          </FontAwesomeIcon>
          <Text variant="body2">Add Address</Text>
        </div>
      </FormFieldSection>
      <FormFieldSection title="Inbound Port Details">
        {params?.portDetails?.map((port, idx) => (
          <Card key={idx} className={classes.portFieldCard}>
            <div className={classes.portFieldContainer}>
              <TextField
                id={`portDetails.${idx}.name`}
                label="Name"
                onChange={(value) => {
                  portDetailsOps.updateAt({ idx, field: port, values: { name: value } })
                }}
                value={port?.name}
                info="The port name should start with a lowercase letter and can contain letters, numbers and '-'"
                required
                placeholder="e.g. port-80"
                validations={[nameValidator('Load Balancer Port name is invalid')]}
              />
              <TextField
                id={`portDetails.${idx}.portRange`}
                label="Port"
                onChange={(value) => {
                  portDetailsOps?.updateAt({ idx, field: port, values: { portRange: value } })
                }}
                value={port?.portRange}
                placeholder="e.g. 80 or 1 - 65535"
                required
                validations={[portRangeValidator]}
              />
              <DropdownField
                DropdownComponent={ProtocolPicklist}
                name={`portDetails.${idx}.protocol`}
                id={`portDetails.${idx}.protocol`}
                label="Protocol"
                compact={false}
                onChange={(value) => {
                  portDetailsOps?.updateAt({ idx, field: port, values: { protocol: value } })
                }}
                value={port?.protocol}
                required
              />

              <TextField
                id={`portDetails.${idx}.remoteAddressPool`}
                label="IP Address or CIDR"
                onChange={(value) => {
                  portDetailsOps?.updateAt({
                    idx,
                    field: port,
                    values: { remoteAddressPool: value },
                  })
                }}
                value={port?.remoteAddressPool}
                placeholder="eg. 12.12.11.12"
                required
                validations={[validIpv4Validator]}
                info="Enter the source IPs from which incoming traffic is allowed."
              />

              <div
                className={classes.removePortField}
                onClick={() => portDetailsOps?.removeAt(idx)}
              >
                <FontAwesomeIcon className={classes.icon} size="lg" solid>
                  circle-minus
                </FontAwesomeIcon>
                <Text variant="body2">Remove Inbound Port Detail</Text>
              </div>
            </div>
          </Card>
        ))}

        <div className={classes.operationLabel} onClick={portDetailsOps.add}>
          <FontAwesomeIcon className={classes.icon} size="lg" solid>
            circle-plus
          </FontAwesomeIcon>
          <Text variant="body2">Add Inbound Port Detail</Text>
        </div>
      </FormFieldSection>
    </ModalForm>
  )
}

export default ConfigureMetalLBModal

const useStyles = makeStyles<Theme>((theme) => ({
  operationLabel: {
    width: 'fit-content',
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'pointer',
    gap: theme.spacing(1),
  },
  removePair: {
    marginTop: theme.spacing(4.5),
    cursor: 'pointer',
  },
  icon: {
    color: theme.components.badge.primary.color,
  },
  addressFieldContainer: {
    display: 'flex',
    gap: 16,
  },
  portFieldContainer: {
    display: 'grid',
    gap: 16,
    maxWidth: '400px',
  },
  removePortField: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  portFieldCard: {
    maxWidth: '400px',
  },
}))
